/**
 * AdminShortcuts — manage system-wide default shortcut tiles.
 * - Defaults are auto-applied to new users on first GET of /Api/shortcuts.
 * - "Push naar bestaande gebruikers" copies missing defaults (matched by URL) to all users.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, CircularProgress, Tooltip, Alert,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, DragIndicator as DragIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import {
  T, Icon, COLORS, ICONS, hostnameOf, faviconUrl, ShortcutDialog,
} from '../CockpitHome/shortcutsCommon';

const API = `${BACKEND_URL}/Api/shortcuts/defaults`;
const RESET_ALL_API = `${BACKEND_URL}/Api/shortcuts/admin/reset-all`;

export default function AdminShortcuts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(false);
  const reorderTimer = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const fetchDefaults = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(API);
      if (!r.ok) throw new Error();
      const data = await r.json();
      setItems(data.defaults || []);
    } catch {
      toast.error('Kon defaults niet laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDefaults(); }, [fetchDefaults]);

  const persistReorder = useCallback((orderedIds) => {
    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(async () => {
      try {
        await authFetch(`${API}/reorder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ordered_ids: orderedIds }),
        });
      } catch { toast.error('Volgorde niet opgeslagen'); }
    }, 300);
  }, []);

  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setItems(prev => {
      const oldIdx = prev.findIndex(x => x.id === active.id);
      const newIdx = prev.findIndex(x => x.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      const next = arrayMove(prev, oldIdx, newIdx);
      persistReorder(next.map(x => x.id));
      return next;
    });
  };

  const startNew = () => setEditing({
    id: `new-${Date.now()}`, name: '', url: '', mode: 'favicon',
    color: COLORS[0], icon: ICONS[0], isNew: true,
  });

  const startEdit = (item) => setEditing({ ...item });

  const remove = async (id) => {
    if (!(await bevestig({ titel: 'Deze default verwijderen?', tekst: 'Bestaande gebruikers behouden hun kopie.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    const prev = items;
    setItems(p => p.filter(x => x.id !== id));
    try {
      const r = await authFetch(`${API}/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error();
      toast.success('Verwijderd');
    } catch {
      setItems(prev);
      toast.error('Verwijderen mislukt');
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const name = editing.name?.trim() || hostnameOf(editing.url) || 'Shortcut';
    const url = editing.url?.trim();
    if (!url) return;
    const payload = {
      name, url,
      mode: editing.mode || 'favicon',
      color: editing.color || null,
      icon: editing.icon || null,
    };
    try {
      if (editing.isNew) {
        const r = await authFetch(API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, position: items.length }),
        });
        if (!r.ok) throw new Error();
        const data = await r.json();
        if (data.default) setItems(prev => [...prev, data.default]);
        toast.success('Toegevoegd');
      } else {
        const r = await authFetch(`${API}/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error();
        const data = await r.json();
        if (data.default) setItems(prev => prev.map(x => x.id === data.default.id ? data.default : x));
        toast.success('Opgeslagen');
      }
      setEditing(null);
    } catch {
      toast.error('Opslaan mislukt');
    }
  };

  const resetAll = async () => {
    if (!(await bevestig({
      titel: 'Alle persoonlijke shortcuts van ALLE gebruikers wissen?',
      tekst: 'Standaard shortcuts blijven staan en verschijnen automatisch weer in elk dashboard. ' +
        'Persoonlijke tegels die gebruikers zelf hebben toegevoegd, gaan verloren.',
      knop: 'Wissen',
      gevaarlijk: true,
    }))) return;
    setResetting(true);
    try {
      const r = await authFetch(RESET_ALL_API, { method: 'POST' });
      if (!r.ok) throw new Error();
      const data = await r.json();
      toast.success(`Reset gelukt — ${data.deleted} persoonlijke tegels verwijderd`);
    } catch {
      toast.error('Reset mislukt');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
            Standaard Shortcuts
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 600 }}>
            Deze tegels staan vast vooraan in het dashboard van elke gebruiker — ze kunnen ze
            niet bewerken, verplaatsen of verwijderen. Wijzigingen hier zijn meteen bij iedereen zichtbaar.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="warning"
            startIcon={resetting ? <CircularProgress size={14} /> : <ResetIcon />}
            onClick={resetAll} disabled={resetting} size="small">
            Reset alle gebruikers
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={startNew} size="small"
            sx={{ bgcolor: T.primary, '&:hover': { bgcolor: '#465469' } }}>
            Toevoegen
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={20} />
        </Box>
      ) : items.length === 0 ? (
        <Alert severity="info" variant="outlined">
          Nog geen default shortcuts. Voeg er één toe — alle nieuwe gebruikers krijgen ze automatisch.
        </Alert>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <Paper variant="outlined">
              {items.map((item, idx) => (
                <DefaultRow
                  key={item.id} item={item}
                  isLast={idx === items.length - 1}
                  onEdit={() => startEdit(item)}
                  onRemove={() => remove(item.id)}
                />
              ))}
            </Paper>
          </SortableContext>
        </DndContext>
      )}

      <ShortcutDialog editing={editing} onChange={setEditing} onSave={saveEdit} onCancel={() => setEditing(null)} />
    </Box>
  );
}

function DefaultRow({ item, isLast, onEdit, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? T.surfaceLow : 'transparent',
  };
  const host = hostnameOf(item.url);
  const useIcon = item.mode === 'icon';
  const fav = faviconUrl(item.url);

  return (
    <Box
      ref={setNodeRef} style={style}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25,
        borderBottom: isLast ? 'none' : `1px solid ${T.surfaceHigh}`,
      }}
    >
      <Box {...attributes} {...listeners}
        sx={{ cursor: 'grab', color: T.outlineVariant, display: 'flex', '&:active': { cursor: 'grabbing' } }}>
        <DragIcon fontSize="small" />
      </Box>
      <Box sx={{
        width: 32, height: 32, borderRadius: '0.5rem', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: useIcon ? (item.color || T.primary) : T.surfaceLow,
        color: useIcon ? '#fff' : T.text, overflow: 'hidden',
      }}>
        {useIcon ? (
          <Icon name={item.icon || 'link'} size={18} fill />
        ) : fav ? (
          <img src={fav} alt="" width={20} height={20} style={{ display: 'block' }} />
        ) : (
          <Icon name="link" size={18} />
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontFamily: T.font, fontSize: '0.88rem', fontWeight: 700, color: T.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
          {item.name}
        </Typography>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {host || '—'} <span style={{ opacity: 0.6 }}>· {item.url}</span>
        </Typography>
      </Box>
      <Tooltip title="Bewerken" arrow>
        <IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton>
      </Tooltip>
      <Tooltip title="Verwijderen" arrow>
        <IconButton size="small" onClick={onRemove} sx={{ color: '#a83836' }}><DeleteIcon fontSize="small" /></IconButton>
      </Tooltip>
    </Box>
  );
}
